import { apiFetch as fetch } from './apiConfig';
import { auth } from './firebase';

export type OtpChannel = 'SMS' | 'EMAIL';

export interface SendOtpResponse {
  success: boolean;
  verificationId?: string;
  destinationMasked?: string;
  expiresAt?: string;
  expiresInSeconds?: number;
  cooldownSeconds?: number;
  message: string;
  code?: string;
  error?: { code?: string; message?: string } | string;
}

export interface VerifyOtpResponse {
  success: boolean;
  verified?: boolean;
  message: string;
  code?: string;
  error?: { code?: string; message?: string } | string;
}

export interface StartDeleteResponse {
  success: boolean;
  verificationId?: string;
  destinationMasked?: string;
  expiresAt?: string;
  channel?: OtpChannel;
  message: string;
  code?: string;
}

export interface ConfirmDeleteResponse {
  success: boolean;
  profileStatus?: string;
  message: string;
  code?: string;
}

async function buildAuthenticatedHeaders(userId?: string): Promise<Record<string, string>> {
  const token = await auth.currentUser?.getIdToken();
  const resolvedUserId =
    userId ||
    auth.currentUser?.uid ||
    localStorage.getItem('pairup_companion_user_id') ||
    '';

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  if (resolvedUserId) {
    headers['x-user-id'] = resolvedUserId;
  }

  return headers;
}

export async function sendOtp(
  channel: OtpChannel,
  purpose: string = 'ACCOUNT_DELETE',
  userId?: string
): Promise<SendOtpResponse> {
  const headers = await buildAuthenticatedHeaders(userId);
  const response = await fetch('/api/auth/otp/send', {
    method: 'POST',
    headers,
    body: JSON.stringify({ channel, purpose }),
  });
  return response.json();
}

export async function verifyOtp(
  verificationId: string,
  otp: string,
  userId?: string
): Promise<VerifyOtpResponse> {
  const headers = await buildAuthenticatedHeaders(userId);
  const response = await fetch('/api/auth/otp/verify', {
    method: 'POST',
    headers,
    body: JSON.stringify({ verificationId, otp }),
  });
  return response.json();
}

export async function startAccountDelete(
  channel: OtpChannel = 'SMS',
  userId?: string
): Promise<StartDeleteResponse> {
  const headers = await buildAuthenticatedHeaders(userId);
  const response = await fetch('/api/companion/account/delete/start', {
    method: 'POST',
    headers,
    body: JSON.stringify({ channel }),
  });
  return response.json();
}

export async function confirmAccountDelete(
  verificationId: string,
  otp: string,
  userId?: string
): Promise<ConfirmDeleteResponse> {
  const headers = await buildAuthenticatedHeaders(userId);
  const response = await fetch('/api/companion/account/delete/confirm', {
    method: 'POST',
    headers,
    body: JSON.stringify({ verificationId, otp }),
  });
  return response.json();
}

export async function sendRegistrationOtpRequest(phone: string): Promise<SendOtpResponse> {
  const cleanPhone = (phone || '').trim();
  try {
    const response = await fetch('/api/v1/auth/registration-otp/send', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: cleanPhone, mobile: cleanPhone }),
    });
    let data: any = null;
    try {
      data = await response.json();
    } catch {
      return {
        success: false,
        code: 'OTP_SERVICE_UNREACHABLE',
        message: 'Could not reach the OTP verification server. Please check your connection and try again.',
      };
    }
    const errMsg =
      typeof data?.error === 'object' && data.error !== null
        ? data.error.message
        : typeof data?.error === 'string'
        ? data.error
        : data?.message;
    if (!response.ok || !data?.success || typeof data.verificationId !== 'string') {
      return {
        success: false,
        code: data?.code || data?.error?.code || 'OTP_SEND_FAILED',
        message: errMsg || 'Failed to send verification code. Please check your mobile number and try again.',
      };
    }
    return {
      success: true,
      verificationId: data.verificationId,
      destinationMasked: data.destinationMasked,
      expiresAt: data.expiresAt,
      expiresInSeconds: data.expiresInSeconds,
      cooldownSeconds: data.cooldownSeconds || 60,
      message: data.message || 'Verification code sent to your mobile number.',
    };
  } catch (err: any) {
    return {
      success: false,
      code: 'NETWORK_ERROR',
      message: err?.message || 'Unable to connect to the verification service. Please try again.',
    };
  }
}

export async function verifyRegistrationOtpRequest(
  phone: string,
  verificationId: string,
  otp: string
): Promise<VerifyOtpResponse> {
  const cleanPhone = (phone || '').trim();
  const cleanOtp = (otp || '').trim();
  try {
    const response = await fetch('/api/v1/auth/registration-otp/verify', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone: cleanPhone,
        mobile: cleanPhone,
        verificationId: (verificationId || '').trim(),
        otp: cleanOtp,
      }),
    });
    let data: any = null;
    try {
      data = await response.json();
    } catch {
      return {
        success: false,
        code: 'OTP_SERVICE_UNREACHABLE',
        message: 'Could not reach the OTP verification server. Please try again.',
      };
    }
    const errMsg =
      typeof data?.error === 'object' && data.error !== null
        ? data.error.message
        : typeof data?.error === 'string'
        ? data.error
        : data?.message;
    if (!response.ok || !data?.success) {
      return {
        success: false,
        code: data?.code || data?.error?.code || 'OTP_VERIFY_FAILED',
        message: errMsg || 'Invalid or expired verification code. Please check the SMS and try again.',
      };
    }
    return {
      success: true,
      verified: true,
      message: data.message || 'Mobile OTP verified successfully.',
    };
  } catch (err: any) {
    return {
      success: false,
      code: 'NETWORK_ERROR',
      message: err?.message || 'Unable to verify code right now. Please try again.',
    };
  }
}

export async function sendPasswordResetOtpRequest(phone: string): Promise<SendOtpResponse> {
  const cleanPhone = (phone || '').trim();
  try {
    const response = await fetch('/api/v1/auth/password-reset/send-otp', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ phone: cleanPhone, mobile: cleanPhone }),
    });
    let data: any = null;
    try {
      data = await response.json();
    } catch {
      return {
        success: false,
        code: 'OTP_SERVICE_UNREACHABLE',
        message: 'Could not reach the password reset server. Please check your connection and try again.',
      };
    }
    const errMsg =
      typeof data?.error === 'object' && data.error !== null
        ? data.error.message
        : typeof data?.error === 'string'
        ? data.error
        : data?.message;
    if (!response.ok || !data?.success || typeof data.verificationId !== 'string') {
      return {
        success: false,
        code: data?.code || data?.error?.code || 'OTP_SEND_FAILED',
        message: errMsg || 'Failed to send password reset code.',
      };
    }
    return {
      success: true,
      verificationId: data.verificationId,
      destinationMasked: data.destinationMasked,
      expiresAt: data.expiresAt,
      expiresInSeconds: data.expiresInSeconds,
      cooldownSeconds: data.cooldownSeconds || 60,
      message: data.message || 'Password reset code sent to your mobile number.',
    };
  } catch (err: any) {
    return {
      success: false,
      code: 'NETWORK_ERROR',
      message: err?.message || 'Unable to connect to the password reset service. Please try again.',
    };
  }
}

export async function confirmPasswordResetWithOtpRequest(params: {
  phone: string;
  verificationId: string;
  otp: string;
  newPassword: string;
}): Promise<VerifyOtpResponse & { email?: string }> {
  const cleanPhone = (params.phone || '').trim();
  const cleanOtp = (params.otp || '').trim();
  try {
    const response = await fetch('/api/v1/auth/password-reset/confirm', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        phone: cleanPhone,
        mobile: cleanPhone,
        verificationId: (params.verificationId || '').trim(),
        otp: cleanOtp,
        newPassword: params.newPassword,
      }),
    });
    let data: any = null;
    try {
      data = await response.json();
    } catch {
      return {
        success: false,
        code: 'OTP_SERVICE_UNREACHABLE',
        message: 'Could not reach the password reset server. Please try again.',
      };
    }
    const errMsg =
      typeof data?.error === 'object' && data.error !== null
        ? data.error.message
        : typeof data?.error === 'string'
        ? data.error
        : data?.message;
    if (!response.ok || !data?.success) {
      return {
        success: false,
        code: data?.code || data?.error?.code || 'PASSWORD_RESET_FAILED',
        message: errMsg || 'Failed to reset password. Please check your verification code.',
      };
    }
    return {
      success: true,
      verified: true,
      email: typeof data.email === 'string' ? data.email : undefined,
      message: data.message || 'Password reset successfully.',
    };
  } catch (err: any) {
    return {
      success: false,
      code: 'NETWORK_ERROR',
      message: err?.message || 'Unable to reset password right now. Please try again.',
    };
  }
}
